/**
 * FocusAreaWorkspace — outer route for /focus-areas/:code? .
 *
 * If `code` is absent in URL params, shows a FA picker. Otherwise renders
 * FAWorkspaceOverview + FANotificationsPanel for the given FA.
 *
 * Mirrors mockup §6100-6462.
 */

import { useNavigate, useParams } from 'react-router-dom'
import { makeStyles } from '@fluentui/react-components'
import { ActivityFeed } from '../components/ActivityFeed'
import { FAWorkspaceOverview } from '../components/FAWorkspaceOverview'
import { CouncilGettingStarted } from '../components/GettingStartedCard'
import { FOCUS_AREAS } from '../types/focus_area'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '1280px',
    margin: '0 auto',
    width: '100%',
  },
  pickerWrap: {
    background: '#FFFFFF',
    border: '1px solid #e6e6e6',
    borderRadius: '8px',
    padding: '24px',
  },
  pickerTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: 0,
  },
  pickerHelp: {
    fontSize: '13px',
    color: '#616161',
    margin: '8px 0 16px',
  },
  pickerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
  },
  pickerCard: {
    background: '#fafafa',
    border: '1px solid #e6e6e6',
    borderRadius: '6px',
    padding: '16px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'box-shadow 120ms, border-color 120ms',
    ':hover': {
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
      background: '#FFFFFF',
      boxShadow: '0 2px 8px rgba(20,58,111,0.10)',
    },
  },
  pickerCode: {
    fontSize: '11px',
    fontWeight: 700,
    color: bayerColors.navyMid,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },
  pickerName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#212121',
    marginTop: '4px',
  },
  crossFaTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: '0 0 12px',
  },
})

export function FocusAreaWorkspace() {
  const styles = useStyles()
  const { code } = useParams<{ code?: string }>()
  const navigate = useNavigate()
  const user = useEffectiveUser()

  // No auto-redirect for council any more (#1220): this page IS the cross-FA
  // overview now, and redirecting its main audience away made it unreachable.
  // Their own FA is one click in the grid below.
  if (!code) {
    const isCouncilLevel = user?.role === 'council' || user?.role === 'admin' || user?.role === 'super_admin'
    return (
      <div className={styles.root}>
        <section className={styles.pickerWrap} data-testid="fa-picker">
          <h1 className={styles.pickerTitle}>Focus Area Workspace</h1>
          <p className={styles.pickerHelp}>Pick a focus area to open its operating view.</p>
          <div className={styles.pickerGrid}>
            {FOCUS_AREAS.map((fa) => (
              <button
                key={fa.code}
                type="button"
                className={styles.pickerCard}
                onClick={() => navigate(`/focus-areas/${fa.code}`)}
                data-testid={`fa-pick-${fa.code}`}
              >
                <div className={styles.pickerCode}>{fa.code}</div>
                <div className={styles.pickerName}>{fa.name}</div>
              </button>
            ))}
          </div>
        </section>
        {isCouncilLevel && (
          <section className={styles.pickerWrap} data-testid="cross-fa-activity">
            <h2 className={styles.crossFaTitle}>Latest across all focus areas</h2>
            <ActivityFeed showFa />
          </section>
        )}
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {/* Only on a council member's own area: that is their Home. */}
      {user?.role === 'council' && user.focus_area_code === code && (
        <CouncilGettingStarted focusArea={code} />
      )}
      <FAWorkspaceOverview
        code={code}
        readOnly={!!user?.views_all_demands && (user.role === 'member' || user.role === 'talent')}
      />
      {/* FANotificationsPanel hidden per #757 — paired delivery, no user control */}
    </div>
  )
}

export default FocusAreaWorkspace
